import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  fetchWasserijProducts,
  setItemPackage,
  type WasserijItemPackage,
  type WasserijProduct,
} from '../../lib/wasserij-items';
import { formatCurrency } from '../../lib/format';

interface Props {
  itemId: string;
  parentProductId: string;
  currentPackage: WasserijItemPackage | null;
  onChanged: () => void;
}

export function PackageEditor({ itemId, parentProductId, currentPackage, onChanged }: Props) {
  const [editing, setEditing] = useState(false);
  const [products, setProducts] = useState<WasserijProduct[] | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing || products) return;
    let cancelled = false;
    setLoadingProducts(true);
    setProductsError(null);
    fetchWasserijProducts('package', parentProductId)
      .then((list) => {
        if (cancelled) return;
        setProducts(list);
        const current = list.find((p) => p.code === currentPackage?.code);
        setDraftId(current?.id ?? null);
      })
      .catch((err: Error) => {
        if (!cancelled) setProductsError(err.message || 'Kon pakketten niet laden');
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, products, parentProductId, currentPackage?.code]);

  const currentProductId = products?.find((p) => p.code === currentPackage?.code)?.id ?? null;
  const dirty = editing && products != null && draftId !== currentProductId;

  const handleApply = async () => {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await setItemPackage(itemId, draftId);
      setEditing(false);
      setProducts(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pakket-mutatie mislukt');
    } finally {
      setSaving(false);
    }
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(false);
    setError(null);
    setProducts(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium">Pakket</h3>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-indigo-600 hover:underline"
          >
            Wijzigen
          </button>
        )}
      </div>

      {!editing ? (
        currentPackage ? (
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-slate-900">{currentPackage.name}</span>
            <span className="text-slate-700 font-mono">{formatCurrency(currentPackage.priceInclBtw)}</span>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Geen pakket geselecteerd.</p>
        )
      ) : (
        <div className="space-y-2">
          {loadingProducts && <p className="text-xs text-slate-500">Pakketten laden…</p>}
          {productsError && <p className="text-xs text-red-600">{productsError}</p>}
          {products && (
            <div className="space-y-1.5">
              <button
                type="button"
                disabled={saving}
                onClick={() => setDraftId(null)}
                className={clsx(
                  'w-full text-left text-sm px-3 py-2 rounded-md border transition-colors',
                  draftId === null
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                    : 'border-slate-200 hover:border-slate-300'
                )}
              >
                Geen pakket
              </button>
              {products.length === 0 && (
                <p className="text-xs text-slate-500 px-1">
                  Geen pakketten beschikbaar voor dit hoofdproduct.
                </p>
              )}
              {products.map((p) => {
                const isCurrent = currentPackage?.code === p.code;
                const isDraft = draftId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={saving}
                    onClick={() => setDraftId(p.id)}
                    className={clsx(
                      'w-full text-left text-sm px-3 py-2 rounded-md border transition-colors flex items-center justify-between gap-2',
                      isDraft
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <span className={isDraft ? 'text-indigo-800 font-medium' : 'text-slate-900'}>
                      {p.name}
                    </span>
                    {isCurrent && <span className="text-[10px] text-slate-500 flex-shrink-0">huidige</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={closeEditor}
              disabled={saving}
              className="text-xs text-slate-600 hover:underline disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => void handleApply()}
              disabled={saving || !dirty}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded disabled:opacity-50"
            >
              {saving ? 'Opslaan…' : 'Toepassen'}
            </button>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
